/**
 * LLM Service for AI-powered product creation
 * Supports multiple providers with fallback and cost optimization
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { getOrganizationContext } from '@/lib/auth';
import {
  validateAndTransformProduct,
  type ProductSearchResult as ValidatedProductSearchResult,
} from '../schemas/productSchema';
import { logger } from '@/lib/logger';

export interface LLMProvider {
  name: 'openai' | 'claude' | 'groq' | 'mistral';
  costPerRequest: number;
  speedRating: 'fast' | 'medium' | 'slow';
  accuracyRating: 'high' | 'medium' | 'low';
  useCase: string[];
}

// Re-export the validated type from schema
export type ProductSearchResult = ValidatedProductSearchResult;

export interface ImageSearchResult {
  url: string;
  source: string;
  confidence: number;
  dimensions: { width: number; height: number };
  type: 'product' | 'diagram' | 'lifestyle';
  angle: 'front' | 'angle' | 'back' | 'side' | 'detail';
  license?: string;
}

function normalizeBrand(candidate: string | null | undefined, query: string): string {
  const cleaned = (candidate || '').trim();
  const invalid = !cleaned || /\d/.test(cleaned);

  if (!invalid) {
    return cleaned;
  }

  const queryHead = query.trim().split(/\s+/)[0] || '';
  if (queryHead && /[a-zA-Z]/.test(queryHead) && !/\d/.test(queryHead)) {
    return capitalize(queryHead);
  }

  return 'Unknown';
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function normalizeSpecPayload(data: any) {
  const specifications = normalizeSpecifications(data?.specifications ?? {});
  const io = normalizeIO(data?.io);
  const features = Array.isArray(data?.features) ? data.features : [];
  const confidence = typeof data?.confidence === 'number' ? data.confidence : 0.5;

  return { specifications, io, features, confidence };
}

function normalizeSpecifications(specs: any) {
  const normalized = { ...specs };

  if (specs?.dimensions) {
    normalized.dimensions = {
      width: toNumberOrNull(specs.dimensions.width),
      height: toNumberOrNull(specs.dimensions.height),
      depth: toNumberOrNull(specs.dimensions.depth),
      unit: specs.dimensions.unit || 'mm',
    };
  }

  if (specs?.weight) {
    normalized.weight = {
      value: toNumberOrNull(specs.weight.value),
      unit: specs.weight.unit || 'kg',
    };
  }

  if (specs?.power) {
    normalized.power = {
      consumption: toNumberOrNull(specs.power.consumption),
      unit: specs.power.unit || 'W',
      voltage: specs.power.voltage || null,
    };
  }

  const numericKeys = ['wattage', 'lumens', 'channels', 'wattage_per_channel'];
  numericKeys.forEach(key => {
    if (key in specs) {
      normalized[key] = toNumberOrNull(specs[key]);
    }
  });

  return normalized;
}

function normalizeIO(io: any) {
  if (!io) {
    return { inputs: [], outputs: [] };
  }

  const sanitize = (items: any[]) =>
    Array.isArray(items)
      ? items
          .map(item => ({
            name: item?.name || '',
            type: item?.type || 'audio',
            connector: item?.connector || '',
          }))
          .filter(item => item.name && item.connector)
      : [];

  return {
    inputs: sanitize(io.inputs),
    outputs: sanitize(io.outputs),
  };
}

function toNumberOrNull(value: any) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function isSpecResultEmpty(result: { specifications: any; io: any; features: any[] }) {
  const hasDims =
    result.specifications?.dimensions?.width ||
    result.specifications?.dimensions?.height ||
    result.specifications?.dimensions?.depth;
  const hasPower = result.specifications?.power?.consumption;
  const hasWeight = result.specifications?.weight?.value;
  const hasNumeric = ['wattage', 'lumens', 'channels', 'wattage_per_channel'].some(key =>
    Boolean(result.specifications?.[key]),
  );
  const hasIO = (result.io?.inputs?.length ?? 0) > 0 || (result.io?.outputs?.length ?? 0) > 0;
  const hasFeatures = result.features.length > 0;

  return !(hasDims || hasPower || hasWeight || hasNumeric || hasIO || hasFeatures);
}

class LLMService {
  private async getClient(): Promise<any> {
    return getAuthenticatedSupabaseClient();
  }

  private providers: LLMProvider[] = [
    {
      name: 'openai',
      costPerRequest: 0.03,
      speedRating: 'medium',
      accuracyRating: 'high',
      useCase: ['complex_products', 'verification', 'spec_extraction', 'initial_search'],
    },
    {
      name: 'groq',
      costPerRequest: 0.001,
      speedRating: 'fast',
      accuracyRating: 'medium',
      useCase: ['initial_search', 'bulk_import', 'spec_extraction'],
    },
    // Other providers disabled until edge functions are deployed
    // {
    //   name: 'claude',
    //   costPerRequest: 0.01,
    //   speedRating: 'medium',
    //   accuracyRating: 'high',
    //   useCase: ['spec_extraction', 'io_parsing']
    // }
  ];

  /**
   * Search for product specifications using AI
   */
  async searchProduct(
    query: string,
    options: {
      provider?: LLMProvider['name'];
      useCache?: boolean;
      includePricing?: boolean;
      useUnified?: boolean;
      includeVariations?: boolean;
    } = {},
  ): Promise<ProductSearchResult> {
    const { provider = 'openai', useCache = true, useUnified = false } = options;

    const supabase = await this.getClient();

    // Use unified edge function if requested
    if (useUnified) {
      try {
        logger.debug('Using unified product search edge function...');
        const { data, error } = await supabase.functions.invoke('product-search-unified', {
          body: { query },
        });

        if (error) throw error;

        // Transform unified response to match expected format
        const result: ProductSearchResult = {
          ...data,
          // If image was uploaded, store it for later use
          ...(data.image?.url && { primary_image_url: data.image.url }),
        };

        // Validate and transform the result
        const validated = validateAndTransformProduct(result) ?? result;

        // Log usage
        await this.logUsage('product_search', 'unified', query, true, false);

        return validated;
      } catch (error) {
        logger.error('Unified search failed, falling back to regular search:', error);
        // Fall through to regular search
      }
    }

    // Check cache first
    if (useCache) {
      const cached = await this.checkCache(query, provider);
      if (cached) {
        await this.updateCacheHit(cached.id);
        // @ts-expect-error - parsed_data not in generated types
        return (cached as any).parsed_data as ProductSearchResult;
      }
    }

    // Call the appropriate provider
    const rawResult = await this.callProvider(provider, query, options);

    // ===============================================
    // NEW: Validate and post-process the provider data
    // This step fixes missing brand/model and normalises
    // categories so downstream code (image search, UI)
    // always receives a complete record.
    // ===============================================
    const result = validateAndTransformProduct(rawResult) ?? rawResult;

    // Handle primary_image_url if provided by the LLM
    if ((rawResult as any).primary_image_url) {
      try {
        logger.debug('Using primary_image_url from LLM:', (rawResult as any).primary_image_url);
        const { data: cacheData, error: cacheErr } = await supabase.functions.invoke(
          'cache-product-image',
          {
            body: { url: (rawResult as any).primary_image_url },
          },
        );
        if (!cacheErr && cacheData?.publicUrl) {
          // Store the cached URL in the result
          (result as any).primary_image_url = cacheData.publicUrl;
        }
      } catch (e) {
        logger.warn('Failed to cache primary image:', e);
      }
    }

    // Cache successful results
    if (result.confidence > 0.7) {
      await this.cacheResult(query, provider, result);
    }

    // Log usage for cost tracking
    await this.logUsage('product_search', provider, query, true, !useCache);

    return result;
  }

  /**
   * Search for products by pattern matching
   */
  async searchProductsByPattern(query: string): Promise<ProductSearchResult[]> {
    try {
      logger.debug('Searching for products with Google:', query);

      const supabase = await this.getClient();
      // Use Google search for real product results
      const { data, error } = await supabase.functions.invoke('google-product-search', {
        body: { query },
      });

      if (error) throw error;

      // Convert matches to ProductSearchResult format
      const results: ProductSearchResult[] = [];
      for (const match of data.matches || []) {
        const productResult: ProductSearchResult = {
          basic: {
            brand: normalizeBrand(match.brand, query),
            model: match.model,
            partNumber: match.partNumber,
            category: match.category || 'other',
            subcategory: null,
            description: match.description,
          },
          specifications: {},
          io: null,
          pricing: null,
          confidence: match.confidence || 0.8,
          provider: 'google-search',
          // Store source URL for reference
          ...(match.source && { source_url: match.source }),
        };

        const validated = validateAndTransformProduct(productResult);
        if (validated) {
          results.push({
            ...validated,
            basic: {
              ...validated.basic,
              brand: normalizeBrand(validated.basic.brand, query),
            },
          });
        }
      }

      logger.debug(`Found ${results.length} real products from Google search`);
      return results;
    } catch (error) {
      logger.error('Google product search failed:', error);
      // Don't fallback to LLM - better to show no results than fake ones
      return [];
    }
  }

  /**
   * Fetch product specifications from web sources
   */
  async fetchProductSpecs(
    brand: string,
    model: string,
    sources: string[] = [],
  ): Promise<{
    specifications: any;
    io: any;
    features?: string[];
    confidence: number;
  }> {
    try {
      logger.debug('Fetching product specs:', { brand, model, sources });

      const supabase = await this.getClient();
      const { data, error } = await supabase.functions.invoke('fetch-product-specs', {
        body: { brand, model, sources },
      });

      if (error) throw error;

      let normalized = normalizeSpecPayload(data);

      if (isSpecResultEmpty(normalized)) {
        logger.debug('Primary spec extraction empty, falling back to scrape-product-specs');
        const { data: scraped, error: scrapeError } = await supabase.functions.invoke(
          'scrape-product-specs',
          {
            body: { brand, model },
          },
        );

        if (!scrapeError) {
          normalized = normalizeSpecPayload({
            specifications: scraped?.specifications,
            io: scraped?.io,
            features: scraped?.features,
            confidence: scraped?.confidence ?? 0.4,
          });
        } else {
          logger.warn('scrape-product-specs fallback failed:', scrapeError);
        }
      }

      return {
        specifications: normalized.specifications,
        io: normalized.io,
        features: normalized.features,
        confidence: normalized.confidence,
      };
    } catch (error) {
      logger.warn('Failed to fetch product specs (edge function may not be configured):', error);
      return {
        specifications: {},
        io: { inputs: [], outputs: [] },
        confidence: 0.3,
      };
    }
  }

  /**
   * Search for product images
   */
  async searchProductImages(
    brand: string,
    model: string,
    query?: string,
  ): Promise<ImageSearchResult[]> {
    try {
      // If a specific query is provided, use it. Otherwise construct from brand/model
      const searchQuery = query || `${brand} ${model}`.trim();

      const supabase = await this.getClient();
      const { data, error } = await supabase.functions.invoke('search-product-images', {
        body: { query: searchQuery, brand, model },
      });
      if (error) throw error;

      const images: ImageSearchResult[] = data.images || [];
      logger.debug('Raw images from search-product-images:', images);

      // Cache each image through edge function to avoid hot-link issues
      const cached: ImageSearchResult[] = [];
      logger.debug('Caching images through edge function...');

      for (const img of images) {
        try {
          logger.debug('Caching image:', img.url);
          const { data: cacheData, error: cacheErr } = await supabase.functions.invoke(
            'cache-product-image',
            {
              body: { url: img.url },
            },
          );
          if (cacheErr) {
            logger.error('cache-product-image error:', cacheErr);
            throw cacheErr;
          }
          if (cacheData?.fallback) {
            logger.warn(
              'cache-product-image returned fallback, skipping:',
              img.url,
              cacheData.reason,
            );
            continue;
          }

          if (cacheData?.publicUrl) {
            logger.debug('Cached image URL:', cacheData.publicUrl);
            cached.push({ ...img, url: cacheData.publicUrl });
          } else {
            logger.warn('No publicUrl in cache response:', cacheData);
          }
        } catch (cacheFail) {
          logger.warn('cache-product-image failed for', img.url, cacheFail);
        }
      }

      logger.debug('Final cached images:', cached);
      await this.logUsage('image_search', 'google', searchQuery, true, true);

      // Fall back to original URLs if caching failed for all images
      if (cached.length === 0) {
        logger.warn('No images were cached successfully, returning original URLs');
        return images;
      }

      return cached;
    } catch (error) {
      logger.error('Image search failed:', error);
      await this.logUsage('image_search', 'google', searchQuery, false, true);
      return [];
    }
  }

  /**
   * Bulk import products
   */
  async bulkImportProducts(
    products: string[],
    options: {
      provider?: LLMProvider['name'];
      batchSize?: number;
      onProgress?: (progress: number) => void;
    } = {},
  ): Promise<ProductSearchResult[]> {
    const { provider = 'groq', batchSize = 10, onProgress } = options;
    const results: ProductSearchResult[] = [];

    for (let i = 0; i < products.length; i += batchSize) {
      const batch = products.slice(i, i + batchSize);

      // Process batch in parallel
      const batchResults = await Promise.all(
        batch.map(p => this.searchProduct(p, { provider, useCache: true })),
      );

      results.push(...batchResults);

      // Update progress
      if (onProgress) {
        onProgress((i + batch.length) / products.length);
      }
    }

    return results;
  }

  /**
   * Call the specific LLM provider
   */
  private async callProvider(
    provider: LLMProvider['name'],
    query: string,
    options: any,
  ): Promise<ProductSearchResult> {
    logger.debug(`Calling ${provider} with query:`, query);
    const supabase = await this.getClient();

    const { data, error } = await supabase.functions.invoke(`llm-${provider}`, {
      body: {
        action: 'search_product',
        query,
        options,
      },
    });

    logger.debug(`${provider} response:`, data, error);

    if (error) {
      logger.error(`${provider} call failed:`, error);
      // Fallback to next provider
      return this.fallbackToNextProvider(provider, query, options);
    }

    // Validate and transform the response
    const validated = validateAndTransformProduct({
      ...data,
      provider,
      confidence: this.calculateConfidence(data),
    });

    if (!validated) {
      logger.error('Invalid product data from provider:', provider);
      return this.fallbackToNextProvider(provider, query, options);
    }

    return validated;
  }

  /**
   * Fallback to next best provider
   */
  private async fallbackToNextProvider(
    failedProvider: string,
    query: string,
    options: any,
  ): Promise<ProductSearchResult> {
    const nextProvider = this.providers.find(
      p => p.name !== failedProvider && p.useCase.includes('spec_extraction'),
    );

    if (!nextProvider) {
      // Return a basic result structure when all providers fail
      // Try to parse brand from query
      const queryParts = query.split(' ');
      const brand = queryParts[0] || '';
      const model = queryParts.slice(1).join(' ') || query;

      return {
        basic: {
          brand: brand,
          model: model,
          partNumber: null,
          category: 'other',
          subcategory: null,
          description: 'Failed to extract specifications',
        },
        specifications: {
          dimensions: null,
          weight: null,
          power: null,
        },
        io: null,
        pricing: null,
        confidence: 0.5,
        provider: 'fallback',
      } as ProductSearchResult;
    }

    return this.callProvider(nextProvider.name, query, options);
  }

  /**
   * Check cache for existing results
   */
  private async checkCache(query: string, provider: string) {
    const normalizedQuery = this.normalizeQuery(query);
    const supabase = await this.getClient();

    // @ts-expect-error - cache table not in generated types yet
    const { data } = await (supabase as any)
      .from('product_search_cache')
      .select('*')
      .eq('normalized_query', normalizedQuery)
      .eq('llm_provider', provider)
      .gt('expires_at', new Date().toISOString())
      .limit(1);

    return data?.[0] || null;
  }

  /**
   * Update cache hit count
   */
  private async updateCacheHit(cacheId: string) {
    const supabase = await this.getClient();
    // @ts-expect-error - cache table not in generated types yet
    await (supabase as any)
      .from('product_search_cache')
      .update({ last_accessed_at: new Date().toISOString() })
      .eq('id', cacheId);
  }

  /**
   * Cache successful results
   */
  private async cacheResult(query: string, provider: string, result: ProductSearchResult) {
    try {
      const normalizedQuery = this.normalizeQuery(query);
      const supabase = await this.getClient();

      // @ts-expect-error - cache table not in generated types yet
      const { error } = await (supabase as any).from('product_search_cache').insert({
        search_query: query,
        normalized_query: normalizedQuery,
        llm_provider: provider,
        parsed_data: result,
        raw_response: result, // Add raw_response field
        confidence_score: result.confidence,
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });

      if (error) {
        logger.error('Cache insert error:', error);
      }
    } catch (error) {
      logger.error('Failed to cache result:', error);
      // Don't throw - caching is not critical
    }
  }

  /**
   * Sanitize result to remove any potential PII or sensitive data
   */
  private sanitizeForCache(result: ProductSearchResult): ProductSearchResult {
    return {
      ...result,
      // Remove any fields that might contain PII
      pricing: result.pricing
        ? {
            ...result.pricing,
            // Only keep structured pricing data
            msrp: result.pricing.msrp,
            currency: result.pricing.currency,
            asOf: null, // Remove specific dates that might identify when/where data was scraped
          }
        : null,
      // Clean specifications to only include structured data
      specifications: Object.keys(result.specifications || {}).reduce((acc, key) => {
        const value = result.specifications[key];
        // Only keep known specification types
        if (
          [
            'dimensions',
            'weight',
            'power',
            'wattage',
            'lumens',
            'color_temp',
            'beam_angle',
          ].includes(key)
        ) {
          acc[key] = value;
        }
        return acc;
      }, {} as any),
    };
  }

  /**
   * Log AI usage for cost tracking
   */
  private async logUsage(
    actionType: string,
    provider: string,
    query: string,
    success: boolean,
    cached: boolean,
  ) {
    try {
      const { userId, organizationId } = await getOrganizationContext();
      if (!userId) return;

      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        logger.debug('Skipping AI usage log insert in local environment.');
        return;
      }

      const costEstimate = cached
        ? 0
        : this.providers.find(p => p.name === provider)?.costPerRequest || 0;
      const supabase = await this.getClient();

      // @ts-expect-error - usage log table not in generated types yet
      const payload: Record<string, any> = {
        user_id: userId,
        action_type: actionType,
        llm_provider: provider,
        query_text: query,
        success,
        cached,
        cost_estimate: costEstimate,
      };

      if (organizationId) {
        payload.workspace_id = organizationId;
      }

      const { error } = await (supabase as any).from('ai_usage_logs').insert(payload);

      if (error) {
        if (error.code === 'PGRST204' || String(error.message || '').includes('organization_id')) {
          logger.debug('Skipping AI usage log insert (column missing locally).');
          return;
        }
        logger.error('Usage log error:', error);
      }
    } catch (error) {
      logger.error('Failed to log usage:', error);
      // Don't throw - logging is not critical
    }
  }

  /**
   * Calculate confidence score based on data completeness
   */
  private calculateConfidence(data: any): number {
    let score = 0;
    let factors = 0;

    // Check basic info
    if (data.basic?.brand && data.basic?.model) {
      score += 0.3;
      factors++;
    }

    // Check specifications
    if (data.specifications && Object.keys(data.specifications).length > 3) {
      score += 0.3;
      factors++;
    }

    // Check I/O
    if (data.io?.inputs?.length > 0 || data.io?.outputs?.length > 0) {
      score += 0.2;
      factors++;
    }

    // Check pricing
    if (data.pricing?.msrp) {
      score += 0.2;
      factors++;
    }

    return factors > 0 ? score : 0.5;
  }

  /**
   * Normalize query for caching
   */
  private normalizeQuery(query: string): string {
    return query.toLowerCase().trim().replace(/\s+/g, ' ');
  }

  /**
   * Get monthly usage for current user
   */
  async getMonthlyUsage(): Promise<{
    count: number;
    estimatedCost: number;
    limit: number;
  }> {
    const { userId } = await getOrganizationContext();
    if (!userId) throw new Error('Not authenticated');

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const supabase = await this.getClient();
    const { data, error } = await (supabase as any)
      .from('ai_usage_logs')
      .select('cost_estimate')
      .eq('user_id', userId)
      .gte('created_at', startOfMonth.toISOString());

    if (error) throw error;

    const logs = (data || []) as any[];
    const count = logs.length;
    const estimatedCost = logs.reduce((sum, log) => sum + (log.cost_estimate || 0), 0);

    // TODO: Get limit from user subscription
    const limit = 100; // Default free tier limit

    return { count, estimatedCost, limit };
  }
}

export const llmService = new LLMService();
